import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  startPlaygroundRun,
  type PlaygroundOptions,
  type PlaygroundRun,
} from '@api/demand-playground';
import PromptEditor from './PromptEditor';
import {
  buildRunBody,
  defaultModel,
  errorText,
  startingPrompts,
} from '../utils/playground';

/** The server's cap on a run name (CharField max_length=200). */
export const NAME_MAX = 200;

interface Props {
  options: PlaygroundOptions;
  onStarted: (run: PlaygroundRun) => void | Promise<void>;
}

/**
 * The run form (ZLAYT-1660): test case, model, optional style example,
 * name and the prompts, prefilled from the current ones.
 */
function RunForm({ options, onStarted }: Props) {
  const [caseId, setCaseId] = useState<number | ''>(options.cases[0]?.id ?? '');
  const [model, setModel] = useState(defaultModel(options.models));
  const [useStyleExample, setUseStyleExample] = useState(false);
  const [name, setName] = useState('');
  const [prompts, setPrompts] = useState(() =>
    startingPrompts(options.current_prompts)
  );
  const [starting, setStarting] = useState(false);
  // Guards a second click landing before React re-renders the button.
  const startingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const run = () => {
    if (startingRef.current || caseId === '') return;
    startingRef.current = true;
    setStarting(true);
    setError(null);
    startPlaygroundRun(
      buildRunBody({
        caseId,
        name,
        model,
        useStyleExample: options.style_example_available && useStyleExample,
        prompts,
      })
    )
      .then((started) => onStarted(started))
      .catch((err) => setError(errorText(err)))
      .finally(() => {
        startingRef.current = false;
        setStarting(false);
      });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h4" fontWeight={500}>
        Try new prompts
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
        }}
      >
        <FormControl size="small" fullWidth>
          <InputLabel id="playground-case-label">Test case</InputLabel>
          <Select
            labelId="playground-case-label"
            label="Test case"
            data-testid="playground-case"
            value={caseId}
            onChange={(e) => setCaseId(Number(e.target.value))}
          >
            {options.cases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel id="playground-model-label">AI model</InputLabel>
          <Select
            labelId="playground-model-label"
            label="AI model"
            data-testid="playground-model"
            value={model}
            onChange={(e) => setModel(String(e.target.value))}
          >
            {options.models.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Name this try"
          placeholder="For example: shorter medical section"
          value={name}
          inputProps={{ maxLength: NAME_MAX, 'aria-label': 'Name this try' }}
          onChange={(e) => setName(e.target.value)}
        />
        {options.style_example_available ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={useStyleExample}
                onChange={(e) => setUseStyleExample(e.target.checked)}
              />
            }
            label="Use the style example"
          />
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h5" fontWeight={500} sx={{ flexGrow: 1 }}>
          Prompts
        </Typography>
        <Button
          size="small"
          onClick={() => setPrompts(startingPrompts(options.current_prompts))}
        >
          Reset to current
        </Button>
      </Stack>
      <PromptEditor
        prompts={prompts}
        order={options.sections}
        onChange={setPrompts}
      />

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="contained"
          onClick={run}
          disabled={starting || caseId === '' || !model}
        >
          Run
        </Button>
        {starting ? <CircularProgress size={18} /> : null}
        <Typography variant="body2" sx={{ color: '#475467' }}>
          Writes a letter with these prompts and one with the current prompts.
          Takes about 2 to 4 minutes.
        </Typography>
      </Stack>
    </Stack>
  );
}

export default RunForm;
